import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash.debounce'
import { stimulus } from '~/init'

class IframeDimensionsController extends Controller {
  static values = {
    frameId: String
  }

  initialize () {
    this.debouncedSend = debounce(() => this.sendHeight(), 150)
  }

  connect () {
    this.lastHeight = null
    this.abortController = new AbortController()
    this.setupListeners()

    // Fallback: send height after 1 second for backward compatibility
    setTimeout(() => {
      this.sendHeight(true)
    }, 1000)

    // Listen for init message from parent
    this.handleParentInit = this.handleParentInit.bind(this)
    window.addEventListener('message', this.handleParentInit, {
      signal: this.abortController.signal
    })

    document.addEventListener('turbo:morph', this.sendHeight.bind(this))
  }

  handleParentInit (event) {
    if (event.data?.type === 'safari-embed:init') {
      this.sendHeight(true)
    }
  }

  disconnect () {
    this.abortController?.abort()
    this.resizeObserver?.disconnect()
  }

  sendHeight (force = false) {
    // The parent snaps the frame to device pixels and can land a fraction short, which brings back the scrollbar.
    const height = document.body ? Math.ceil(document.body.getBoundingClientRect().height) + 1 : 0

    // Send if forced or height changed
    if (force || height !== this.lastHeight) {
      this.lastHeight = height

      window.parent.postMessage({
        type: 'iframe:height',
        payload: { frameHeight: height, id: this.frameIdValue }
      }, '*')
    }
  }

  setupListeners () {
    const { signal } = this.abortController

    // Listen to window resize and load events
    window.addEventListener('resize', this.debouncedSend, { signal })
    window.addEventListener('load', this.debouncedSend, { signal })

    // Observe DOM changes for height updates
    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(this.debouncedSend)
      const target = document.body || document.documentElement
      if (target) this.resizeObserver.observe(target)
    }
  }
}

stimulus.register('iframe-dimensions', IframeDimensionsController)
